import { useState } from 'react';
import { MapPin, Clock, DollarSign, ExternalLink, Heart, HeartOff, X } from 'lucide-react';
import { clsx } from 'clsx';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { StrategyBadge } from './StrategyBadge';
import { SavedJobBucketChip } from '../ui/StatusChip';
import type { Job, SavedJob, JobMatch } from '../../types';

interface JobCardProps {
  job: Job | SavedJob | JobMatch;
  variant?: 'default' | 'compact' | 'detailed';
  showActions?: boolean;
  onSave?: (jobId: string) => void;
  onReject?: (jobId: string) => void;
  onApply?: (jobId: string) => void;
  onClick?: (jobId: string) => void;
  className?: string;
}

export function JobCard({
  job,
  variant = 'default',
  showActions = true,
  onSave,
  onReject,
  onApply,
  onClick,
  className,
}: JobCardProps) {
  const [isLoading, setIsLoading] = useState<'save' | 'reject' | 'apply' | null>(null);

  const handleAction = async (action: 'save' | 'reject' | 'apply', handler?: (jobId: string) => void) => {
    if (!handler) return;
    
    setIsLoading(action);
    try {
      await handler(job.id);
    } finally {
      setIsLoading(null);
    }
  };

  const formatSalary = (min?: number, max?: number): string => {
    if (!min && !max) return '';
    
    const formatAmount = (amount: number) => {
      if (amount >= 1000000) return `$${(amount / 1000000).toFixed(1)}M`;
      if (amount >= 1000) return `$${(amount / 1000).toFixed(0)}k`;
      return `$${amount.toLocaleString()}`;
    };

    if (min && max) {
      return `${formatAmount(min)} - ${formatAmount(max)}`;
    }
    if (min) {
      return `${formatAmount(min)}+`;
    }
    return `Up to ${formatAmount(max!)}`;
  };

  const isMatch = 'match_score' in job;
  const isSaved = 'bucket' in job;

  return (
    <Card 
      className={clsx(
        'transition-all duration-200 hover:shadow-lg',
        onClick && 'cursor-pointer hover:ring-2 hover:ring-primary/20',
        className
      )}
      onClick={onClick ? () => onClick(job.id) : undefined}
    >
      <div className="space-y-4">
        {/* Header with title and company */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-foreground text-lg leading-tight line-clamp-2">
                {job.title}
              </h3>
              {job.attribution && (
                <StrategyBadge attribution={job.attribution} />
              )}
              {isSaved && (
                <SavedJobBucketChip bucket={(job as SavedJob).bucket} />
              )}
            </div>
            
            <div className="flex items-center gap-2 mt-1">
              <p className="text-muted-foreground font-medium">{job.company}</p>
              {job.logo_url && (
                <img 
                  src={job.logo_url} 
                  alt={`${job.company} logo`}
                  className="w-4 h-4 rounded"
                />
              )}
            </div>
          </div>

          {isMatch && (
            <div className="text-right">
              <div className="text-sm font-semibold text-primary">
                {Math.round((job as JobMatch).match_score * 100)}% match
              </div>
            </div>
          )}
        </div>

        {/* Job details */}
        <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
          <div className="flex items-center gap-1">
            <MapPin className="h-4 w-4" />
            <span>{job.location}</span>
          </div>
          
          {job.postedAt && (
            <div className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              <span>{job.postedAt}</span>
            </div>
          )}
          
          {(job.salaryMin || job.salaryMax) && (
            <div className="flex items-center gap-1">
              <DollarSign className="h-4 w-4" />
              <span>{formatSalary(job.salaryMin, job.salaryMax)}</span>
            </div>
          )}

          {job.remote && (
            <Badge variant="secondary" className="text-xs">
              Remote
            </Badge>
          )}

          {job.employment_type && (
            <Badge variant="secondary" className="text-xs">
              {job.employment_type}
            </Badge>
          )}
        </div>

        {/* Description */}
        {job.description && variant !== 'compact' && (
          <p className="text-muted-foreground text-sm line-clamp-3">
            {job.description}
          </p>
        )}

        {/* Attribution rationale */}
        {job.attribution?.rationale && variant === 'detailed' && (
          <div className="bg-muted/30 rounded-xl p-3">
            <p className="text-xs text-muted-foreground">
              <strong>Why this job:</strong> {job.attribution.rationale}
            </p>
          </div>
        )}

        {/* Match reasons */}
        {isMatch && (job as JobMatch).match_reasons && variant !== 'compact' && (
          <div className="flex flex-wrap gap-1">
            {(job as JobMatch).match_reasons.slice(0, 3).map((reason, index) => (
              <Badge key={index} variant="secondary" className="text-xs">
                {reason}
              </Badge>
            ))}
            {(job as JobMatch).match_reasons.length > 3 && (
              <Badge variant="secondary" className="text-xs">
                +{(job as JobMatch).match_reasons.length - 3} more
              </Badge>
            )}
          </div>
        )}

        {/* Tags */}
        {job.tags && job.tags.length > 0 && variant !== 'compact' && (
          <div className="flex flex-wrap gap-1">
            {job.tags.slice(0, 5).map((tag, index) => (
              <Badge key={index} variant="secondary" className="text-xs">
                {tag}
              </Badge>
            ))}
            {job.tags.length > 5 && (
              <Badge variant="secondary" className="text-xs">
                +{job.tags.length - 5} more
              </Badge>
            )}
          </div>
        )}

        {/* Actions */}
        {showActions && (
          <div className="flex items-center justify-between pt-2 border-t border-border">
            <div className="flex items-center gap-2">
              {onSave && (
                <Button
                  variant={job.saved ? "secondary" : "ghost"}
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAction('save', onSave);
                  }}
                  disabled={isLoading === 'save'}
                  className="gap-1"
                >
                  {job.saved ? (
                    <>
                      <HeartOff className="h-4 w-4" />
                      Unsave
                    </>
                  ) : (
                    <>
                      <Heart className="h-4 w-4" />
                      Save
                    </>
                  )}
                </Button>
              )}

              {job.url && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    window.open(job.url, '_blank');
                  }}
                  className="gap-1"
                >
                  <ExternalLink className="h-4 w-4" />
                  View Job
                </Button>
              )}
            </div>

            <div className="flex items-center gap-2">
              {onReject && !job.rejected && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAction('reject', onReject);
                  }}
                  disabled={isLoading === 'reject'}
                  className="text-muted-foreground hover:text-destructive"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}

              {onApply && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAction('apply', onApply);
                  }}
                  disabled={isLoading === 'apply' || job.applied}
                >
                  {job.applied ? 'Applied' : 'Apply'}
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Saved job metadata */}
        {isSaved && (job as SavedJob).notes && variant !== 'compact' && (
          <div className="text-sm text-muted-foreground bg-muted/30 rounded-xl p-3">
            <strong>Notes:</strong> {(job as SavedJob).notes}
          </div>
        )}
      </div>
    </Card>
  );
}